import React from "react";
import PropTypes from "prop-types";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const getFileName = (value) => {
  if (!value || typeof value !== "string") return "";

  const sanitizedValue = value.split("?")[0].split("#")[0];

  return sanitizedValue.split("/").pop()?.toLowerCase() || "";
};

const Image = ({
  className,
  image,
  imageData,
  src,
  alt,
  style,
  loading,
  objectFit,
  objectPosition,
  imgStyle,
  ...otherProps
}) => {
  // `imageData` lets callers pass ready-made gatsby image data, e.g. an
  // art-directed desktop/mobile pair built with withArtDirection().
  const imgData = imageData || getImage(image?.localFile || image);
  const fallbackSrc =
    src ||
    image?.sourceUrl ||
    image?.mediaItemUrl ||
    image?.localFile?.publicURL;
  const optimizedSrc = imgData?.images?.fallback?.src;
  const sourceFileName = getFileName(fallbackSrc);
  const optimizedFileName = getFileName(optimizedSrc);
  const hasMismatchedOptimizedImage =
    sourceFileName && optimizedFileName && sourceFileName !== optimizedFileName;
  const fallbackAlt = alt || image?.altText || fallbackSrc || "image";
  // An explicit `loading` prop wins. (Previously `loading || ... ? "eager" : "lazy"`
  // turned any truthy value, including "lazy", into "eager".)
  const imageLoading =
    loading || (className?.includes("eager") ? "eager" : "lazy");

  if (imgData && !hasMismatchedOptimizedImage) {
    return (
      <GatsbyImage
        className={className}
        imgClassName={className}
        style={style}
        imgStyle={imgStyle || style}
        image={imgData}
        alt={fallbackAlt}
        loading={imageLoading}
        objectFit={objectFit || "cover"}
        objectPosition={objectPosition || image?.focalPoint?.objectPosition}
        {...otherProps}
      />
    );
  }

  if (!fallbackSrc) return null;

  return (
    <img
      className={className}
      style={style}
      src={fallbackSrc}
      alt={fallbackAlt}
      loading={imageLoading}
      decoding="async"
      {...otherProps}
    />
  );
};

export default Image;

Image.propTypes = {
  className: PropTypes.string,
  image: PropTypes.object,
  imageData: PropTypes.object,
  src: PropTypes.string,
  alt: PropTypes.string,
  style: PropTypes.object,
  loading: PropTypes.string,
  objectFit: PropTypes.string,
  objectPosition: PropTypes.string,
  imgStyle: PropTypes.object,
};
